import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

// ONE reusable scroll-story engine. A fixed central statement stays anchored while
// supporting "expressions" of the GME world progressively reveal and layer in around it.
// variant: "center" (scattered) | "right" (weighted to the right side).

const POS = {
  center: [
    { t: "7%", l: "15%" }, { t: "6%", l: "77%" }, { t: "36%", l: "4%" },
    { t: "36%", l: "88%" }, { t: "68%", l: "13%" }, { t: "70%", l: "81%" },
  ],
  right: [
    { t: "8%", l: "56%" }, { t: "24%", l: "80%" }, { t: "42%", l: "60%" },
    { t: "58%", l: "82%" }, { t: "72%", l: "58%" }, { t: "14%", l: "90%" },
    { t: "50%", l: "92%" }, { t: "66%", l: "74%" },
  ],
};

// Each supporting image slides IN toward the central statement as the section scrolls.
// Visibility (opacity) is intentionally NOT staggered so all items appear together early;
// only the inward slide is staggered. Fully scroll-linked (reverses on scroll-up).
const StoryItem = ({ progress, index, total, pos, item }) => {
  const s = index * 0.05;
  const e = s + 0.35;
  const dx = parseFloat(pos.l) - 50;
  const dy = parseFloat(pos.t) - 50;
  const len = Math.hypot(dx, dy) || 1;
  const dist = 150;
  const outX = (dx / len) * dist;
  const outY = (dy / len) * dist;
  const x = useTransform(progress, [s, e], [outX, 0]);
  const y = useTransform(progress, [s, e], [outY, 0]);
  // Function-form transforms (not keyframe arrays): framer's keyframe-array path
  // produced a phantom reverse fade (exit) on this MotionValue — the function
  // clamps 0→1 by progress 0.12 and stays 1 forever afterwards.
  const opacity = useTransform(progress, (p) => Math.min(1, Math.max(0, p / 0.12)));
  const scale = useTransform(progress, (p) => Math.min(1, 0.7 + 0.3 * Math.max(0, p / 0.2)));
  const blurN = useTransform(progress, (p) => Math.max(0, 8 * (1 - Math.min(1, p / 0.22))));
  // Blur only during the entrance, then removed entirely ("none") once settled —
  // a lingering blur(0px) filter forces a permanent compositing layer that makes
  // images and text render soft/washed out.
  const filter = useTransform(blurN, (b) => (b > 0.5 ? `blur(${b}px)` : "none"));
  return (
    <motion.div style={{ x, y, opacity, scale, filter, top: pos.t, left: pos.l }}
      className="absolute w-32 lg:w-40">
      {item.img && (
        <div className="rounded-2xl overflow-hidden ring-1 ring-[#C9A24B]/25 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.9)] aspect-[4/5] mb-2.5">
          <img src={item.img} alt={item.label} loading="lazy" className="w-full h-full object-cover" />
        </div>
      )}
      <p className="font-display text-base lg:text-lg text-cream/90 leading-tight">{item.label}</p>
      {item.sub && <p className="text-xs text-cream/45 mt-0.5">{item.sub}</p>}
    </motion.div>
  );
};

const Central = ({ eyebrow, title, titleGold, sub, stack }) => (
  <div className="relative z-20 mx-auto max-w-2xl px-6 text-center">
    <p className="text-xs md:text-sm tracking-[0.34em] uppercase text-[#E7CC86]/75 mb-6">{eyebrow}</p>
    <h2 className={`font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.08] text-cream ${stack ? "whitespace-pre-line" : ""}`}>
      {title}{stack ? "\n" : " "}<span className="text-gold">{titleGold}</span>
    </h2>
    {sub && <p className="mt-6 text-base md:text-lg text-cream/60">{sub}</p>}
  </div>
);

export const ScrollStory = ({ id, testid, eyebrow, title, titleGold, sub, items, variant = "center", stack = false }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const positions = POS[variant] || POS.center;
  const list = items.slice(0, positions.length);
  const height = Math.min(260, 120 + list.length * 26);
  // Circular group sway — the whole constellation of cards rotates together as one
  // body while revealing (all original positions preserved; settles upright at rest).
  // Rotation completes shortly after the cards have entered, then stays put.
  const groupRotate = useTransform(scrollYProgress, [0, 0.55, 0.9], [24, 2, 0]);

  return (
    <section id={id} data-testid={testid} className="relative">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="aurora-weave" />
        <div className="absolute inset-0 hex-texture opacity-40" />
      </div>

      {!reduce && (
        <div ref={ref} className="relative hidden md:block" style={{ height: `${height}vh` }}>
          <div className="sticky top-0 h-screen overflow-hidden flex items-center">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[46rem] h-[46rem] radial-glow-strong blur-3xl pointer-events-none" />
            <div className="relative w-full max-w-7xl mx-auto h-full">
              <motion.div style={{ rotate: groupRotate }} className="absolute inset-0">
                {list.map((it, i) => (
                  <StoryItem key={it.label} progress={scrollYProgress} index={i} total={list.length} pos={positions[i]} item={it} />
                ))}
              </motion.div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Central eyebrow={eyebrow} title={title} titleGold={titleGold} sub={sub} stack={stack} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile + reduced-motion fallback — static, progressively revealed */}
      <div className={`${reduce ? "block" : "md:hidden"} py-24`}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] radial-glow blur-3xl pointer-events-none" />
        <div className="relative">
          <Central eyebrow={eyebrow} title={title} titleGold={titleGold} sub={sub} stack={stack} />
          <div className="mt-12 grid grid-cols-2 gap-4 max-w-lg mx-auto px-5">
            {list.map((it, i) => (
              <motion.div key={it.label} initial={{ opacity: 0, y: 20, rotate: i % 2 ? 8 : -8 }} whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}>
                {it.img && (
                  <div className="rounded-xl overflow-hidden ring-1 ring-[#C9A24B]/25 aspect-[4/5] mb-2">
                    <img src={it.img} alt={it.label} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="font-display text-base text-cream/90 leading-tight">{it.label}</p>
                {it.sub && <p className="text-xs text-cream/45">{it.sub}</p>}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
